import {
  PA_ROUTINE_EVENT_LABELS,
  type PaRoutine,
  type PaRoutineEventType,
} from '@easyflow/shared';

export const WEEKDAYS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '1', label: 'Lunedì' },
  { value: '2', label: 'Martedì' },
  { value: '3', label: 'Mercoledì' },
  { value: '4', label: 'Giovedì' },
  { value: '5', label: 'Venerdì' },
  { value: '6', label: 'Sabato' },
  { value: '0', label: 'Domenica' },
];

export type ScheduleFrequency = 'daily' | 'weekdays' | 'weekly' | 'monthly' | 'custom';

export interface ScheduleDraft {
  kind: 'time' | 'event';
  frequency: ScheduleFrequency;
  time: string;
  weekday: string;
  monthday: string;
  customCron: string;
  eventType: PaRoutineEventType;
}

function pad2(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

export function describeCron(cron: string | null): string {
  if (!cron) return 'Pianificazione non impostata';
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return `Pianificazione personalizzata (${cron})`;
  const [minute = '', hour = '', monthDay = '', month = '', weekDay = ''] = parts;
  const isNumber = (value: string) => /^\d+$/.test(value);
  const time = isNumber(minute) && isNumber(hour)
    ? `${pad2(Number(hour))}:${pad2(Number(minute))}`
    : null;
  if (time && monthDay === '*' && month === '*' && weekDay === '*') return `Ogni giorno alle ${time}`;
  if (time && monthDay === '*' && month === '*' && weekDay === '1-5') return `Nei giorni feriali alle ${time}`;
  if (time && monthDay === '*' && month === '*' && isNumber(weekDay)) {
    const day = WEEKDAYS.find(item => item.value === weekDay)?.label ?? `giorno ${weekDay}`;
    return `Ogni ${day.toLowerCase()} alle ${time}`;
  }
  if (time && isNumber(monthDay) && month === '*' && weekDay === '*') {
    return `Il giorno ${monthDay} di ogni mese alle ${time}`;
  }
  if (minute === '0' && hour === '*' && monthDay === '*' && month === '*' && weekDay === '*') return 'Ogni ora';
  return `Pianificazione personalizzata (${cron})`;
}

export function describeTrigger(routine: PaRoutine): string {
  if (routine.trigger_kind === 'event' && routine.event_type) {
    return `Quando: ${PA_ROUTINE_EVENT_LABELS[routine.event_type]}`;
  }
  return describeCron(routine.cron_expression);
}

export function draftFromRoutine(routine: PaRoutine): ScheduleDraft {
  const base: ScheduleDraft = {
    kind: routine.trigger_kind,
    frequency: 'daily',
    time: '09:00',
    weekday: '1',
    monthday: '1',
    customCron: routine.cron_expression ?? '',
    eventType: routine.event_type ?? 'lead_created',
  };
  if (!routine.cron_expression) return base;
  const parts = routine.cron_expression.trim().split(/\s+/);
  if (parts.length !== 5) return { ...base, frequency: 'custom' };
  const [minute = '', hour = '', monthDay = '', month = '', weekDay = ''] = parts;
  const isNumber = (value: string) => /^\d+$/.test(value);
  if (!isNumber(minute) || !isNumber(hour)) return { ...base, frequency: 'custom' };
  const time = `${pad2(Number(hour))}:${pad2(Number(minute))}`;
  if (monthDay === '*' && month === '*' && weekDay === '*') return { ...base, frequency: 'daily', time };
  if (monthDay === '*' && month === '*' && weekDay === '1-5') return { ...base, frequency: 'weekdays', time };
  if (monthDay === '*' && month === '*' && isNumber(weekDay)) {
    return { ...base, frequency: 'weekly', time, weekday: weekDay };
  }
  if (isNumber(monthDay) && month === '*' && weekDay === '*') {
    return { ...base, frequency: 'monthly', time, monthday: monthDay };
  }
  return { ...base, frequency: 'custom' };
}

export function cronFromDraft(draft: ScheduleDraft): string | null {
  if (draft.frequency === 'custom') return draft.customCron.trim() || null;
  const [hourText = '', minuteText = ''] = draft.time.split(':');
  const hour = Number(hourText);
  const minute = Number(minuteText);
  if (!Number.isInteger(hour) || hour < 0 || hour > 23 || !Number.isInteger(minute) || minute < 0 || minute > 59) {
    return null;
  }
  if (draft.frequency === 'daily') return `${minute} ${hour} * * *`;
  if (draft.frequency === 'weekdays') return `${minute} ${hour} * * 1-5`;
  if (draft.frequency === 'weekly') return `${minute} ${hour} * * ${draft.weekday}`;
  const monthDay = Number(draft.monthday);
  if (!Number.isInteger(monthDay) || monthDay < 1 || monthDay > 31) return null;
  return `${minute} ${hour} ${monthDay} * *`;
}